import Swiper from 'swiper';
import { Navigation, Autoplay } from 'swiper/modules';

const SLIDER_SELECTOR = '.main-block__slider';
const LOAD_LISTENER_FLAG = 'swiperLoadListenerAttached';
const INIT_FLAG = 'swiperInitialized';

const isSliderStructureValid = (heroSlider) => {
  const wrapper = heroSlider.querySelector(':scope > .swiper-wrapper');

  if (!wrapper) {
    return false;
  }

  const slideItems = Array.from(wrapper.children);

  return (
    heroSlider.classList.contains('swiper') &&
    heroSlider.querySelectorAll(':scope > .swiper-wrapper').length === 1 &&
    heroSlider.querySelectorAll('.swiper-wrapper .swiper-wrapper').length === 0 &&
    slideItems.length > 0 &&
    slideItems.every((slide) => slide.classList.contains('swiper-slide'))
  );
};

const getHeroSlider = () => document.querySelector(SLIDER_SELECTOR);

const scheduleInitOnLoad = (heroSlider) => {
  if (document.readyState === 'complete' || heroSlider.dataset[LOAD_LISTENER_FLAG] === 'true') {
    return;
  }

  heroSlider.dataset[LOAD_LISTENER_FLAG] = 'true';

  window.addEventListener(
    'load',
    () => {
      delete heroSlider.dataset[LOAD_LISTENER_FLAG];
      initMainSwiper();
    },
    { once: true }
  );
};

const destroyMainSwiper = () => {
  const heroSlider = getHeroSlider();

  if (!heroSlider) {
    return;
  }

  heroSlider.mainSwiperCleanup?.();

  if (heroSlider.swiper && !heroSlider.swiper.destroyed) {
    heroSlider.swiper.destroy(true, true);
  }
};

export const initMainSwiper = () => {
  const heroSlider = getHeroSlider();

  if (!heroSlider) {
    return;
  }

  if (document.readyState !== 'complete') {
    scheduleInitOnLoad(heroSlider);
    return;
  }

  const sliderRoot = heroSlider.closest('.main-block');
  const currentCounter = sliderRoot?.querySelector('.main-block__counter--current');
  const totalCounter = sliderRoot?.querySelector('.main-block__counter--total');
  const progressBar = sliderRoot?.querySelector('.main-block__progress-bar');
  const prevButton = sliderRoot?.querySelector('.main-block__arrow--prev');
  const nextButton = sliderRoot?.querySelector('.main-block__arrow--next');

  if (
    !sliderRoot ||
    !currentCounter ||
    !totalCounter ||
    !progressBar ||
    !prevButton ||
    !nextButton
  ) {
    return;
  }

  if (!isSliderStructureValid(heroSlider)) {
    return;
  }

  if (
    heroSlider.dataset[INIT_FLAG] === 'true' &&
    heroSlider.swiper &&
    !heroSlider.swiper.destroyed
  ) {
    return;
  }

  heroSlider.mainSwiperCleanup?.();

  if (heroSlider.swiper) {
    heroSlider.swiper.destroy(true, true);
  }

  const updatePagination = (swiper) => {
    const totalSlides = swiper.slides.length;
    const currentSlide = swiper.realIndex + 1;
    const progress = currentSlide / totalSlides;

    currentCounter.textContent = String(currentSlide).padStart(2, '0');
    totalCounter.textContent = String(totalSlides).padStart(2, '0');
    progressBar.style.transform = `scaleX(${progress})`;
  };

  let syncFrameId = 0;

  const scheduleLayoutSync = (swiper, shouldUpdate = false) => {
    window.cancelAnimationFrame(syncFrameId);
    syncFrameId = window.requestAnimationFrame(() => {
      if (swiper.destroyed || heroSlider.clientWidth === 0 || heroSlider.clientHeight === 0) {
        return;
      }

      if (shouldUpdate) {
        swiper.update();
      }

      updatePagination(swiper);
    });
  };

  const swiper = new Swiper(heroSlider, {
    modules: [Navigation, Autoplay],
    speed: 400,
    observer: true,
    observeParents: true,
    resizeObserver: true,
    updateOnWindowResize: true,
    watchOverflow: true,
    autoplay: {
      delay: 8000,
      disableOnInteraction: false,
      pauseOnMouseEnter: false,
    },
    loop: true,
    navigation: {
      nextEl: nextButton,
      prevEl: prevButton,
    },
    on: {
      init(instance) {
        scheduleLayoutSync(instance, true);
      },
      slideChange(instance) {
        updatePagination(instance);
      },
      resize(instance) {
        scheduleLayoutSync(instance, true);
      },
      observerUpdate(instance) {
        scheduleLayoutSync(instance);
      },
    },
  });

  const slideImages = heroSlider.querySelectorAll('.main-block__image');
  const imageListeners = [];

  slideImages.forEach((image) => {
    if (image.complete) {
      return;
    }

    const handleImageReady = () => {
      scheduleLayoutSync(swiper, true);
    };

    image.addEventListener('load', handleImageReady);
    image.addEventListener('error', handleImageReady);
    imageListeners.push([image, handleImageReady]);
  });

  let resizeObserver;
  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(() => {
      scheduleLayoutSync(swiper, true);
    });

    resizeObserver.observe(sliderRoot);
  }

  heroSlider.dataset[INIT_FLAG] = 'true';

  heroSlider.mainSwiperCleanup = () => {
    window.cancelAnimationFrame(syncFrameId);
    resizeObserver?.disconnect();
    imageListeners.forEach(([image, listener]) => {
      image.removeEventListener('load', listener);
      image.removeEventListener('error', listener);
    });
    delete heroSlider.dataset[INIT_FLAG];
    delete heroSlider.mainSwiperCleanup;
  };

  scheduleLayoutSync(swiper, true);
};

if (typeof module !== 'undefined' && module.hot) {
  module.hot.dispose(() => {
    destroyMainSwiper();
  });
}
